import React, { useEffect, useState } from "react";
import { Badge } from "ui/components/badge";
import { DateCell } from "@/modules/work/billables/cells";
import { EmployeeCell } from "components/cell/employee/cell";
import EvtSheet from "components/sheet";
import { Icon } from "components/index";
import { List } from "components";
import classNames from "classnames";
import { X } from "icons/src";
import { StatusCell } from "components/cell/status/cell";

const fields = [
  "id",
  "name",
  "status",
  "coordinator.id",
  "coordinator.first_name",
  "coordinator.last_name",
  "coordinator.avatar",
  "start_date",
  "end_date",
  "billable_hours",
];

const MilestoneInput = ({
  isOpen = false,
  onChange,
  value,
  disabled = false,
  multiselect = false,
  singleSelect = false,
  allowRemoval = false,
  footer,
  children,
  excludedMilestoneIds = [],
  filters = {},
  className,
  jobId,
}) => {
  const [open, setOpen] = useState(isOpen);
  const [selectedMilestones, setSelectedMilestones] = useState([]);

  useEffect(() => {
    if (value) {
      setSelectedMilestones(Array.isArray(value) ? value : [value]);
    }
  }, [value]);

  const handleRemoveMilestone = (milestoneId) => {
    const newSelection = selectedMilestones.filter((m) => m.id !== milestoneId);
    setSelectedMilestones(newSelection);
    if (singleSelect) {
      onChange?.(null);
    } else {
      onChange?.(newSelection);
    }
  };

  const handleListChange = (newSelected) => {
    setSelectedMilestones(newSelected);
    if (singleSelect) {
      onChange?.(newSelected[0] || null);
    } else {
      onChange?.(newSelected);
    }
  };

  const defaultfilter = {
    ...(jobId && { job: { _eq: jobId } }),
    ...filters,
  };

  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
    },
    {
      id: "coordinator",
      header: "MC",
      accessorKey: "coordinator",
      cell: EmployeeCell,
    },
    ...(filters?.status !== "completed"
      ? [
          {
            accessorKey: "status",
            header: "Status",
            cell: StatusCell,
          },
        ]
      : []),
    {
      id: "billable_hours",
      header: "Billable Hours",
      accessorKey: "billable_hours",
      sortable: true,
    },
    {
      id: "start_date",
      header: "Start Date",
      accessorKey: "start_date",
      sortable: true,
      cell: DateCell,
    },
    {
      id: "end_date",
      header: "End Date",
      accessorKey: "end_date",
      sortable: true,
      cell: DateCell,
    },
  ];

  if (excludedMilestoneIds.length > 0) {
    defaultfilter.id = { _nin: excludedMilestoneIds };
  }

  return (
    <EvtSheet
      setOpen={setOpen}
      open={open}
      title="Select Milestone"
      disabled={disabled}
      className="w-[95vw] sm:max-w-[500px] md:max-w-[600px] lg:max-w-[750px]"
      trigger={
        multiselect || singleSelect ? (
          children ? (
            children
          ) : (
            <div
              className={classNames(
                "flex min-h-10 w-full items-center flex-wrap rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                className,
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              {selectedMilestones.map((milestone) => (
                <Badge
                  key={milestone.id}
                  variant="secondary"
                  className="flex items-center gap-1 font-normal"
                >
                  {milestone.name}
                  {allowRemoval && (
                    <div
                      as="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleRemoveMilestone(milestone.id);
                      }}
                    >
                      <Icon
                        icon={X}
                        className="h-3 w-3 hover:text-destructive cursor-pointer"
                      />
                    </div>
                  )}
                </Badge>
              ))}
              {selectedMilestones.length === 0 && (
                <span className="text-muted-foreground">Select</span>
              )}
            </div>
          )
        ) : children ? (
          children
        ) : (
          <div
            className={classNames(
              "flex min-h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm cursor-pointer transition-colors text-muted-foreground",
              className,
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            Select
          </div>
        )
      }
      footer={
        typeof footer === "function"
          ? footer({
              setOpen,
              selectedMilestones,
              setSelectedMilestones,
              setSelectedMilestone: (val) => {
                setSelectedMilestones(val ? [val] : []);
              },
            })
          : footer
      }
    >
      <div className="flex-1 overflow-auto">
        <List
          storageKey={`milestone-input-${jobId}`}
          endPoint="milestones"
          filters={defaultfilter}
          hidePagination={true}
          perPage={100}
          columns={columns}
          meta={{ fields }}
          selection={true}
          rowClassname
          value={selectedMilestones}
          onChange={handleListChange}
          showSearch={false}
        />
      </div>
    </EvtSheet>
  );
};

export default React.memo(MilestoneInput);
